/**
 * Rutele aplicației.
 *
 * Două zone clar separate:
 *  • public (login, înregistrare, resetare, invitație, meniu public) — fără shell de dashboard;
 *  • aplicație (`/app/*`) — doar pentru sesiuni active, cu shell-ul de navigație.
 *
 * Garda de rută este strict o comoditate de UX: fiecare request este oricum verificat server-side
 * (sesiune + rol + apartenență la cont), deci accesul nu depinde de codul din browser.
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell, FullPageLoader } from './components/AppShell.js';
import { useSession } from './lib/session.js';
import { LoginPage } from './pages/Login.js';
import { RegisterPage } from './pages/Register.js';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/Password.js';
import { VerifyEmailPage } from './pages/VerifyEmail.js';
import { InvitePage } from './pages/Invite.js';
import { DashboardPage } from './pages/Dashboard.js';
import { ProductsPage } from './pages/Products.js';
import { MenusPage } from './pages/Menus.js';
import { QrPage } from './pages/QrCodes.js';
import { DisplaysPage } from './pages/Displays.js'
import { OrdersPage } from './pages/Orders.js';
import { PlaylistsPage } from './pages/Playlists.js';
import { AnalyticsPage } from './pages/Analytics.js';
import { PromotionsPage } from './pages/Promotions.js';
import { BillingPage } from './pages/Billing.js';
import { RestaurantsPage } from './pages/Restaurants.js';
import { DomainPage } from './pages/Domain.js';
import { LocationsPage } from './pages/Locations.js';
import { TeamPage } from './pages/Team.js';
import { SettingsPage } from './pages/Settings.js';
import { NotificationsPage } from './pages/Notifications.js';
import { NotFoundPage } from './pages/NotFound.js';
import { LandingPage } from './pages/Landing.js';
import { AdminRoutes } from './pages/admin/Admin.js';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();

  if (session.status === 'loading') return <FullPageLoader />;
  if (session.status === 'anonymous') {
    // Păstrăm destinația pentru a reveni după autentificare.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      {/* public */}
      {/* Pagina publică de prezentare — singura rută „marketing” a produsului. */}
      <Route path="/" element={<LandingPage />} />
      {/* Consola de platformă: shell propriu, gardă pe `isPlatformAdmin` (serverul verifică din nou). */}
      <Route path="/admin/*" element={<AdminRoutes />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* aplicație */}
      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="menus" element={<MenusPage />} />
        <Route path="qr" element={<QrPage />} />
        <Route path="orders" element={<OrdersPage />} />
      <Route path="displays" element={<DisplaysPage />} />
        <Route path="playlists" element={<PlaylistsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="promotions" element={<PromotionsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="restaurants" element={<RestaurantsPage />} />
        <Route path="domain" element={<DomainPage />} />
        <Route path="locations" element={<LocationsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
